import { toast } from "sonner";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { pretty } from "@/components/rcc/Cells";

const sel = "h-9 w-full rounded-md border border-[#27272A] bg-[#18181B] px-2 text-sm text-zinc-200 capitalize";

export const Field = ({ f, value, onChange }) => {
  const [k, label, type] = f;
  const id = `rcc-field-${k}`;
  if (Array.isArray(type)) return <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={sel} data-testid={id}><option value="">Choose…</option>{type.map((o) => <option key={o} value={o}>{pretty(o)}</option>)}</select>;
  if (type === "textarea") return <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-[#09090B]" placeholder={label} data-testid={id} />;
  if (type === "tags") return <Input value={Array.isArray(value) ? value.join(", ") : value || ""} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trimStart()))} placeholder={label} data-testid={id} />;
  return <Input type={type === "number" ? "number" : type === "date" ? "date" : "text"} value={value ?? ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} placeholder={label} data-testid={id} />;
};

const clean = (v) => Object.fromEntries(Object.entries(v).map(([k, x]) => [k, Array.isArray(x) ? x.map((s) => s.trim()).filter(Boolean) : x]).filter(([, x]) => x !== "" && x != null));

export const RecordForm = ({ fields, initial, noun, onSubmit, onClose }) => {
  const objKeys = fields.filter((f) => f[2] === "textarea" && initial?.[f[0]] && typeof initial[f[0]] === "object" && !Array.isArray(initial[f[0]])).map((f) => f[0]);
  const [v, setV] = useState(() => ({ ...(initial || {}), ...Object.fromEntries(objKeys.map((k) => [k, JSON.stringify(initial[k], null, 2)])) }));
  const [busy, setBusy] = useState(false);
  const missing = fields.filter((f) => f[3] && (v[f[0]] == null || v[f[0]] === "" || (Array.isArray(v[f[0]]) && !v[f[0]].filter(Boolean).length)));
  const submit = async (e) => {
    e.preventDefault();
    const out = { ...v };
    for (const k of objKeys) {
      try { const o = JSON.parse(out[k] || "{}"); if (!o || typeof o !== "object" || Array.isArray(o)) throw new Error(); out[k] = o; }
      catch { toast.error(`${fields.find((f) => f[0] === k)[1]} must stay valid JSON ({ ... }).`); return; }
    }
    setBusy(true); try { await onSubmit(clean(out)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#8224e3]/40 bg-[#0f0b16] sm:max-w-lg" data-testid="rcc-form">
        <DialogHeader><DialogTitle className="font-display capitalize">{initial?.id ? "Edit" : "New"} {noun}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {fields.map((f) => <div key={f[0]} className="space-y-1"><label className="text-xs text-zinc-400">{f[1]}{f[3] && <span className="text-[#c79bff]"> *</span>}</label><Field f={f} value={v[f[0]]} onChange={(x) => setV((s) => ({ ...s, [f[0]]: x }))} /></div>)}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || missing.length > 0} className="bg-[#8224e3] text-white hover:bg-[#9440f0]" data-testid="rcc-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
